import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AnimatePresence } from 'framer-motion';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Accounts from './components/Accounts';
import AccountDetail from './components/AccountDetail';
import Transactions from './components/Transactions';
import Activity from './components/Activity';
import EmailSender from './components/EmailSender';
import IncomingEmails from './components/IncomingEmails';
import SupportChats from './components/SupportChats';
import Advertising from './components/Advertising';
import SetupAdmin from './components/SetupAdmin';
import Navbar from './components/Navbar';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/login" element={!user || user.role !== 'admin' ? <Login /> : <Navigate to="/" replace />} />
        <Route path="/setup-admin" element={<SetupAdmin />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <PrivateRoute>
              <Accounts />
            </PrivateRoute>
          }
        />
        <Route
          path="/accounts/:id"
          element={
            <PrivateRoute>
              <AccountDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <PrivateRoute>
              <Transactions />
            </PrivateRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <PrivateRoute>
              <Activity />
            </PrivateRoute>
          }
        />
        <Route
          path="/email"
          element={
            <PrivateRoute>
              <EmailSender />
            </PrivateRoute>
          }
        />
        <Route
          path="/emails"
          element={
            <PrivateRoute>
              <IncomingEmails />
            </PrivateRoute>
          }
        />
        <Route
          path="/support-chats"
          element={
            <PrivateRoute>
              <SupportChats />
            </PrivateRoute>
          }
        />
        <Route
          path="/advertising"
          element={
            <PrivateRoute>
              <Advertising />
            </PrivateRoute>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user && user.role === 'admin';
  const isLoginPage = location.pathname === '/login';
  const isSetupPage = location.pathname === '/setup-admin';

  return (
    <div className={`app-layout ${isAdmin && !isLoginPage && !isSetupPage ? 'has-sidebar' : ''}`}>
      {isAdmin && !isLoginPage && !isSetupPage && <Navbar />}
      <main className={isAdmin && !isLoginPage && !isSetupPage ? 'main-content' : ''} style={!(isAdmin && !isLoginPage && !isSetupPage) ? { flex: 1, width: '100%' } : undefined}>
        <AppRoutes />
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px'
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <AppContent />
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
